/**
 * Generic function to transform provider location data for map display
 * Renders only unique locations on the map rather than all provider-location combinations
 * @param {Array} providers - Array of provider objects with location data
 * @param {Function} coordinateMapper - Function to transform coordinates into the correct format
 * @param {boolean} isAdvancedMarkersEnabled - Whether to include provider counts
 * @returns {Array} - Formatted unique locations for map display
 */
const transformProviderLocations = (
  providers,
  coordinateMapper,
  isAdvancedMarkersEnabled = false
) => {
  const locationHasProviders = new Map();

  const allLocations = (providers || []).flatMap((provider) =>
    (provider.locations || [])
      .filter((location) => {
        if (locationHasProviders.has(location.id)) {
          locationHasProviders.set(
            location.id,
            locationHasProviders.get(location.id) + 1
          );
        } else {
          locationHasProviders.set(location.id, 1);
        }

        return (
          location &&
          (location.coordinates ||
            (location.address && location.address.coordinates))
        );
      })
      .map((location) => ({
        id: location.id,
        name: location.name || provider.name,
        coordinates: coordinateMapper(location)
      }))
  );

  const uniqueLocationsMap = new Map();

  allLocations.forEach((location) => {
    if (!uniqueLocationsMap.has(location.id)) {
      uniqueLocationsMap.set(location.id, {
        id: location.id,
        name: `View providers at ${location.name}`,
        coordinates: location.coordinates,
        providerCount: isAdvancedMarkersEnabled
          ? locationHasProviders.get(location.id) || 1
          : 0
      });
    }
  });

  return Array.from(uniqueLocationsMap.values());
};

/**
 * Transforms provider location data for Google Map
 * @param {Array} providers - Array of provider objects with location data
 * @param {boolean} isAdvancedMarkersEnabled - Whether to include provider counts
 * @returns {Array} - Formatted locations with Google Map coordinate structure (lat, lon)
 */
export const transformLocationsForGoogleMap = (
  providers,
  isAdvancedMarkersEnabled = false
) => {
  return transformProviderLocations(
    providers,
    (location) => {
      const coords =
        location.coordinates ||
        (location.address && location.address.coordinates);
      if (!coords) return { lat: 0, lon: 0 };

      return {
        lat: coords.lat,
        lon: coords.lon || coords.lng
      };
    },
    isAdvancedMarkersEnabled
  );
};

/**
 * Transforms provider location data for AWS Map
 * @param {Array} providers - Array of provider objects with location data
 * @param {boolean} isAdvancedMarkersEnabled - Whether to include provider counts
 * @returns {Array} - Formatted locations with AWS Map coordinate structure (lat, lng)
 */
export const transformLocationsForAwsMap = (
  providers,
  isAdvancedMarkersEnabled = false
) => {
  return transformProviderLocations(
    providers,
    (location) => {
      const coords =
        location.coordinates ||
        (location.address && location.address.coordinates);
      if (!coords) return { lat: 0, lng: 0 };

      return {
        lat: coords.lat,
        lng: coords.lon || coords.lng
      };
    },
    isAdvancedMarkersEnabled
  );
};
